import {
    AgeGroup,
    Category,
    ClassLevel,
    ClassRule,
    CompetitionLevel,
    CompetitionTeamOverride,
    Dance,
    Discipline,
    Division,
    Federation,
    FederationClass,
    FederationClassLicenseItem,
    FederationClassHistory,
    FederationClassMembership,
    FederationLicense,
    FederationLicenseItem,
    FederationOrganization,
    FederationProgress,
    FederationRepresentationHistory,
    FederationUser,
    Organization,
    OrganizationUser,
    Ranking,
    RankingCorrection,
    RankingTeam,
    User
} from '@vote4dance/judging-schema';
import {
    AgeGroupId,
    CategoryId,
    CompetitionId,
    DanceTeamId,
    DisciplineId,
    DivisionId,
    FederationRuleContract as SharedFederationRuleContract,
    FederationRuleDefinition as SharedFederationRuleDefinition,
    FederationRuleProfileEntry as SharedFederationRuleProfileEntry,
    FederationRuleProfile as SharedFederationRuleProfile,
    FederationRankingProfileSummary as SharedFederationRankingProfileSummary,
    FederationClassId,
    FederationId,
    FederationLicenseId,
    OrganizationId,
    RankingId,
    UserId
} from '@vote4dance/judging-types';
import { StreamSource } from "../hooks/streams";

export type FederationItem = Federation
& Pick<FederationUser, 'federation_permissions'>
& {
    ranking_count: number;
    has_licenses: number;
    has_organizations: number;
    has_classes: number;
};

export type FederationFeatureFlags = {
    rankings?: number;
    divisions?: number;
    getting_started?: number;
};

export function parseFederationFeatures(raw?: string | null): FederationFeatureFlags {
    try { return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}

export function isFederationFeatureEnabled(flags: FederationFeatureFlags, key: keyof FederationFeatureFlags): boolean {
    return (flags[key] ?? 0) !== 0;
}

export const FederationSource = new StreamSource<FederationItem[]>("/federations");

export const UserFederationSource = new StreamSource<FederationItem[]>("/user-federations");
export const AllFederationsSource = new StreamSource<FederationItem[]>("/all-federations");

export type FederationPublicLicenseItem = Pick<FederationLicenseItem,
    'license_item_id' | 'federation_id' | 'license_category' | 'license_type' |
    'license_item_code' | 'license_item_name' | 'license_item_kind' |
    'organization_mode' | 'license_item_price' | 'license_item_currency'
>;

export type FederationPublicOrganization = Pick<Organization,
    'organization_id' | 'organization_tag' | 'organization_name' |
    'organization_country' | 'organization_city' | 'organization_email' |
    'organization_phone' | 'organization_link' | 'organization_description'
>;

export type FederationPublicClass = Pick<FederationClass,
    'federation_class_id' | 'federation_class_name' | 'federation_class_code' |
    'discipline_id' | 'federation_class_order'
> & {
    division_id?: DivisionId;
    division_name?: string;
};

export function FederationPublicLicenseItemsSource(federation_id: FederationId) {
    return new StreamSource<FederationPublicLicenseItem[]>(`/federations/${federation_id}/public/license-items`);
}

export function FederationPublicOrganizationsSource(federation_id: FederationId) {
    return new StreamSource<FederationPublicOrganization[]>(`/federations/${federation_id}/public/organizations`);
}

export function FederationPublicClassesSource(federation_id: FederationId) {
    return new StreamSource<FederationPublicClass[]>(`/federations/${federation_id}/public/classes`);
}

export type DisciplineItem = Discipline
& {
    class_children: number
}

export type ClassRuleItem = ClassRule
& {
    [key: string]: string | number | null | undefined;
}

export type FederationUserItem = FederationUser
& Pick<User, 'user_email' | 'user_firstname' | 'user_lastname'>

export type FederationOrganizationItem = FederationOrganization
& Organization
& Pick<OrganizationUser, 'organization_permissions' | 'organization_approved'>

type FederationUserItemCompanion = {
    getFullName(user: FederationUserItem): string
}

export type FederationRuleDefinition = SharedFederationRuleDefinition

export type FederationRuleContract = SharedFederationRuleContract

export type FederationRuleProfile = SharedFederationRuleProfile

export type FederationRuleProfileEntry = SharedFederationRuleProfileEntry

export type FederationRankingProfileSummary = SharedFederationRankingProfileSummary & Ranking

export type FederationClassItem = FederationClass & {
    division_id?: DivisionId
}

export type FederationRankingCorrectionItem = Partial<RankingCorrection> & {
    correction_id: string
    team_name?: string
    competition_name?: string
}

export interface FederationRankingLicensePolicy {
    ranking_license_policy: 'required' | 'optional' | 'disabled'
}

export interface FederationEnforcementLogItem {
    log_id: string
    federation_id: FederationId
    dance_team_id?: DanceTeamId | null
    user_id?: UserId | null
    enforcement_point:
        | 'registration'
        | 'start_confirmation'
        | 'result_publication'
        | 'ranking'
        | 'progression'
        | 'promotion'
        | 'transfer'
        | 'championship_entry'
        | 'championship_qualification'
        | 'international_selection'
    enforcement_action: 'allowed' | 'blocked' | 'excluded' | 'promoted' | 'skipped'
    enforcement_reason: string
    enforcement_data?: string | null
    competition_id?: CompetitionId | null
    competition_name?: string | null
    federation_class_id?: FederationClassId | null
    federation_class_name?: string | null
    license_id?: FederationLicenseId | null
    ranking_id?: RankingId | null
    ranking_name?: string | null
    season_year?: number | null
    created_at?: string | null
    created_by?: UserId | null
    created_by_name?: string | null
    user_full_name?: string | null
    user_email?: string | null
    team_name?: string | null
    team_members?: string | null
}

export const FederationUserItem: FederationUserItemCompanion = {
    getFullName(user: FederationUserItem): string {
        return user.user_firstname + ' ' + user.user_lastname;
    }
}

export function FederationUserSource(federation_id: FederationId) {
    return new StreamSource<FederationUserItem[]>(`/federations/${federation_id}/users`);
}

export function FederationOrganizationSource(federation_id: FederationId) {
    return new StreamSource<FederationOrganizationItem[]>(`/federations/${federation_id}/organizations`);
}

export function DivisionSource(federation_id: FederationId) {
    return new StreamSource<Division[]>(`/federations/${federation_id}/divisions`);
}

export function AgeGroupSource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<AgeGroup[]>(`/federations/${federation_id}/divisions/${division_id}/agegroups`);
}

export function DisciplineSource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<DisciplineItem[]>(`/federations/${federation_id}/divisions/${division_id}/disciplines`);
}

export function CategorySource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<Category[]>(`/federations/${federation_id}/divisions/${division_id}/categories`);
}

export function CompetitionLevelSource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<CompetitionLevel[]>(`/federations/${federation_id}/divisions/${division_id}/competitionlevels`);
}

export function FederationCompetitionLevelSource(federation_id: FederationId) {
    return new StreamSource<CompetitionLevel[]>(`/federations/${federation_id}/competitionlevels`);
}

export function ClassLevelSource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<ClassLevel[]>(`/federations/${federation_id}/divisions/${division_id}/classlevels`);
}

export function FederationClassSource(federation_id: FederationId) {
    return new StreamSource<FederationClassItem[]>(`/federations/${federation_id}/classes`);
}

export function DisciplineClassSource(federation_id: FederationId, division_id: DivisionId, discipline_id: DisciplineId) {
    return new StreamSource<FederationClassItem[]>(`/federations/${federation_id}/divisions/${division_id}/disciplines/${discipline_id}/classes`);
}

export function DivisionClassSource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<FederationClassItem[]>(`/federations/${federation_id}/divisions/${division_id}/classes`);
}

export function FederationRuleProfileSource(federation_id: FederationId, federation_class_id: FederationClassId) {
    return new StreamSource<FederationRuleProfile>(`/federations/${federation_id}/classes/${federation_class_id}/rule-profile`);
}

export function FederationRuleProfilesSource(federation_id: FederationId) {
    return new StreamSource<FederationRuleProfileEntry[]>(`/federations/${federation_id}/rule-profiles`);
}

export const FederationRuleContractSource = new StreamSource<FederationRuleContract>(`/federations/rule-contract`);

export function ClassRuleSource(federation_id: FederationId, division_id: DivisionId, discipline_id: DisciplineId) {
    return new StreamSource<ClassRuleItem[]>(`/federations/${federation_id}/divisions/${division_id}/disciplines/${discipline_id}/rules`);
}

export function DanceSource(federation_id: FederationId, division_id: DivisionId, discipline_id: DisciplineId) {
    return new StreamSource<Dance[]>(`/federations/${federation_id}/divisions/${division_id}/disciplines/${discipline_id}/dances`);
}

export function FederationLicenseSource(federation_id: FederationId) {
    return new StreamSource<FederationLicense[]>(`/federations/${federation_id}/licenses`);
}

export function FederationLicenseItemSource(federation_id: FederationId) {
    return new StreamSource<FederationLicenseItem[]>(`/federations/${federation_id}/license-items`);
}

export function UserFederationLicenseSource(user_id: UserId) {
    return new StreamSource<FederationLicense[]>(`/users/${user_id}/federation-licenses`);
}

export function FederationClassLicenseItemSource(federation_id: FederationId, federation_class_id: FederationClassId) {
    return new StreamSource<FederationClassLicenseItem[]>(`/federations/${federation_id}/classes/${federation_class_id}/license-items`);
}

export function FederationClassMembershipSource(federation_id: FederationId) {
    return new StreamSource<FederationClassMembership[]>(`/federations/${federation_id}/class-memberships`);
}

export function FederationClassHistorySource(federation_id: FederationId) {
    return new StreamSource<FederationClassHistory[]>(`/federations/${federation_id}/class-history`);
}

export function FederationProgressSource(federation_id: FederationId) {
    return new StreamSource<FederationProgress[]>(`/federations/${federation_id}/progress`);
}

export function FederationRepresentationSource(federation_id: FederationId) {
    return new StreamSource<FederationRepresentationHistory[]>(`/federations/${federation_id}/representation`);
}

export function FederationEnforcementLogSource(federation_id: FederationId) {
    return new StreamSource<FederationEnforcementLogItem[]>(`/federations/${federation_id}/enforcement-log`);
}

export function UserFederationEnforcementLogSource(user_id: UserId, federation_id: FederationId) {
    return new StreamSource<FederationEnforcementLogItem[]>(`/users/${user_id}/federations/${federation_id}/enforcement-log`);
}

export function DanceTeamFederationClassMembershipSource(dance_team_id: DanceTeamId) {
    return new StreamSource<FederationClassMembership[]>(`/danceteams/${dance_team_id}/class-memberships`);
}

export function DanceTeamFederationClassHistorySource(dance_team_id: DanceTeamId) {
    return new StreamSource<FederationClassHistory[]>(`/danceteams/${dance_team_id}/class-history`);
}

export function DanceTeamFederationProgressSource(dance_team_id: DanceTeamId) {
    return new StreamSource<FederationProgress[]>(`/danceteams/${dance_team_id}/progress`);
}

export function DanceTeamFederationRepresentationSource(dance_team_id: DanceTeamId) {
    return new StreamSource<FederationRepresentationHistory[]>(`/danceteams/${dance_team_id}/representation`);
}

export function CompetitionTeamOverrideSource(competition_id: CompetitionId) {
    return new StreamSource<CompetitionTeamOverride[]>(`/competitions/${competition_id}/team-overrides`);
}

export function FederationDivisionRankingSummarySource(federation_id: FederationId, division_id: DivisionId) {
    return new StreamSource<FederationRankingProfileSummary[]>(`/federations/${federation_id}/divisions/${division_id}/ranking-profiles`);
}

export function FederationClassRankingSummarySource(federation_id: FederationId, division_id: DivisionId, federation_class_id: FederationClassId) {
    return new StreamSource<FederationRankingProfileSummary[]>(`/federations/${federation_id}/divisions/${division_id}/classes/${federation_class_id}/ranking-profiles`);
}

export function FederationDivisionRankingSummaryItemSource(federation_id: FederationId, division_id: DivisionId, ranking_id: RankingId) {
    return new StreamSource<FederationRankingProfileSummary>(`/federations/${federation_id}/divisions/${division_id}/ranking-profiles/${ranking_id}`);
}

export function FederationDivisionRankingSummaryClassesSource(federation_id: FederationId, division_id: DivisionId, ranking_id: RankingId) {
    return new StreamSource<FederationClass[]>(`/federations/${federation_id}/divisions/${division_id}/ranking-profiles/${ranking_id}/classes`);
}

export function FederationDivisionRankingLicensePolicySource(federation_id: FederationId, division_id: DivisionId, ranking_id: RankingId) {
    return new StreamSource<FederationRankingLicensePolicy>(`/federations/${federation_id}/divisions/${division_id}/ranking-profiles/${ranking_id}/license-policy`);
}

export function FederationDivisionRankingCorrectionSource(federation_id: FederationId, division_id: DivisionId, ranking_id: RankingId) {
    return new StreamSource<FederationRankingCorrectionItem[]>(`/federations/${federation_id}/divisions/${division_id}/ranking-profiles/${ranking_id}/corrections`);
}
